import { PlayerCardDrawings } from '@/API';
import { SubmittedDrawPlayerImage } from '@/utils/submittedDrawing';
import * as d3 from 'd3';
import Konva from 'konva';
import { Stage } from 'konva/lib/Stage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Group, Stage as KStage, Layer, Rect } from 'react-konva';
import { COMPACT_CARD_SIZE, DEFAULT_CARD_SIZE } from './PlayersCards';

// Types
export type Node = {
  id: string;
  card: PlayerCardDrawings;
  x: number;
  y: number;
  fx?: number | null;
  fy?: number | null;
  rotation: number;
};

type Link = {
  source: string;
  target: string;
};

type GraphData = {
  nodes: Node[];
  links: Link[];
};

export type GraphDataExtended = GraphData & {
  width: number;
  height: number;
};

type PlayerCardsSummaryProps = {
  playerCards: PlayerCardDrawings[];
  onUpdateGraphData: (data: GraphDataExtended) => void;
};

/**
 * PlayersCards component displays a network of player cards with interactive features.
 */
export default function PlayerCardsSummary({
  playerCards,
  onUpdateGraphData
}: PlayerCardsSummaryProps) {
  // State
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] });
  const [initialized, setInitialized] = useState(false);
  const [gameContainerDimensions, setGameContainerDimensions] = useState({
    width: 0,
    height: 0
  });

  // Refs
  const stageRef = useRef<Stage | null>(null);
  const simulationRef = useRef<d3.Simulation<Node, undefined> | null>(null);
  const nodeRefs = useRef<{ [key: string]: Konva.Group }>({});
  const initializedNodes = useRef<Set<string>>(new Set());
  const pendingInitializations = useRef<Set<string>>(new Set());

  const smallScreen = window.innerHeight < 849 && window.innerWidth < 1313;
  const showSmallCards = smallScreen || playerCards.length > 10;
  const CARD_SIZE = showSmallCards ? COMPACT_CARD_SIZE : DEFAULT_CARD_SIZE;

  const LINK_MIN_LENGTH = CARD_SIZE.width;

  useEffect(() => {
    onUpdateGraphData?.({
      ...graphData,
      width: gameContainerDimensions.width,
      height: gameContainerDimensions.height
    });
  }, [gameContainerDimensions, graphData, onUpdateGraphData]);

  /**
   * Computes and sets the game area dimensions based on window size.
   */
  const computeGameArea = useCallback(() => {
    setGameContainerDimensions({
      // Note: Buffer on each side of the game area.
      width: Math.round(window.innerWidth * 0.9),

      // Note: Subtracting 100 to account for the bottom navbar.
      height: Math.round(window.innerHeight - 346)
    });
  }, []);

  useEffect(() => {
    computeGameArea();
    window.addEventListener('resize', computeGameArea);
    return () => window.removeEventListener('resize', computeGameArea);
  }, [computeGameArea]);

  useEffect(() => {
    if (graphData.nodes.length > 0) {
      setInitialized(true);
    }
  }, [graphData.nodes]);

  useEffect(() => {
    if (playerCards.length === 0 || gameContainerDimensions.width === 0) return;

    const availableSpace = {
      width: gameContainerDimensions.width - CARD_SIZE.width,
      height: gameContainerDimensions.height - CARD_SIZE.height
    };

    const nodes: Node[] = playerCards.map((card) => {
      const existingNode = graphData.nodes.find((node) => node.id === card.id);
      return {
        id: card.id,
        card,
        x: existingNode
          ? existingNode.x
          : Math.random() * availableSpace.width + CARD_SIZE.width / 2,
        y: existingNode
          ? existingNode.y
          : Math.random() * availableSpace.height + CARD_SIZE.height / 2,
        rotation: existingNode
          ? existingNode.rotation
          : Math.ceil(Math.random() * 10) * (Math.round(Math.random()) ? 1 : -1)
      };
    });

    // Track which nodes need initial scale setup
    const newNodeIds = nodes
      .filter((node) => !initializedNodes.current.has(node.id))
      .map((node) => node.id);

    newNodeIds.forEach((id) => pendingInitializations.current.add(id));

    // Always update state immediately so new cards appear right away
    setGraphData((prevData) => ({ ...prevData, nodes: [...nodes] }));

    if (!initialized) {
      return;
    }

    const links: Link[] = nodes.slice(0, -1).map((node, i) => ({
      source: node.id,
      target: nodes[i + 1].id
    }));

    // Set up D3 force simulation

    const simulation = d3
      .forceSimulation(nodes)

      // Bounding box
      .force('bound', () => {
        for (const node of nodes) {
          const nodeX = node.x - CARD_SIZE.width / 2;
          const nodeY = node.y - CARD_SIZE.height / 2;

          if (nodeX < 25) {
            node.x = 25 + CARD_SIZE.width / 2;
          } else if (nodeX + CARD_SIZE.width > gameContainerDimensions.width) {
            node.x = gameContainerDimensions.width - CARD_SIZE.width - 15 + CARD_SIZE.width / 2;
          }

          if (nodeY < 20) {
            node.y = 20 + CARD_SIZE.height / 2;
          } else if (nodeY + CARD_SIZE.height > gameContainerDimensions.height) {
            node.y = gameContainerDimensions.height - CARD_SIZE.height - 15 + CARD_SIZE.height / 2;
          }
        }
      })

      // Forces the nodes to gravitate towards each other
      .force(
        'link',
        d3
          .forceLink(links)
          .id((d: any) => d.id)
          .distance(LINK_MIN_LENGTH)
          .strength(Math.max(0.001, 0.05 / nodes.length))
      )

      // Forces the nodes to the center of the game area
      .force(
        'center',
        d3
          .forceCenter(gameContainerDimensions.width / 2, gameContainerDimensions.height / 2)
          .strength(0.01)
      )

      .on('tick', () => {
        for (const node of nodes) {
          const group = nodeRefs.current[node.id];
          if (group) {
            group.x(node.x);
            group.y(node.y);
          }
        }

        setGraphData((prevData) => ({ ...prevData, nodes: [...nodes] }));
        stageRef.current?.batchDraw();
      });

    simulationRef.current = simulation;

    return () => {
      simulation.stop();
    };
  }, [playerCards, gameContainerDimensions, initialized]);

  /**
   * Renders a single node (player card) in the graph.
   */
  const renderNode = useCallback(
    (node: Node) => {
      const drawing = JSON.parse(node.card.drawing ?? '{}');

      return (
        <Group
          key={node.id}
          ref={(ref) => {
            if (!initialized) return;

            if (ref && gameContainerDimensions.width > 0) {
              nodeRefs.current[node.id] = ref;

              const isNewNode = !initializedNodes.current.has(node.id);

              ref.offsetX(CARD_SIZE.width / 2); // Move the offset to the centre of the shape
              ref.offsetY(CARD_SIZE.height / 2);

              if (isNewNode) {
                // Initial appearance: set scale to 0 immediately, then animate
                initializedNodes.current.add(node.id);

                ref.scale({ x: 0, y: 0 });

                // Animate to target scale
                ref.to({
                  scaleX: 1,
                  scaleY: 1,
                  duration: 0.5,
                  easing: Konva.Easings.EaseInOut,
                  onFinish: () => {
                    // Remove from pending and restart simulation when all nodes are initialized
                    pendingInitializations.current.delete(node.id);
                    if (pendingInitializations.current.size === 0 && simulationRef.current) {
                      simulationRef.current.alpha(1).restart();
                    }
                  }
                });
              } else {
                if (pendingInitializations.current.has(node.id)) return;
                // Animate scale transition for selection changes
                ref.to({
                  scaleX: 1,
                  scaleY: 1,
                  duration: 0.5,
                  easing: Konva.Easings.EaseInOut
                });
              }
            }
          }}
          x={node.x}
          y={node.y}
          draggable
          onDragMove={(e) => {
            const newX = e.target.x();
            const newY = e.target.y();
            node.x = newX;
            node.y = newY;
            node.fx = newX;
            node.fy = newY;
            simulationRef.current?.alpha(1).restart();
          }}
          onDragEnd={() => {
            node.fx = null;
            node.fy = null;
          }}
          opacity={initializedNodes.current.has(node.id) ? 1 : 0}
          rotation={node.rotation}
        >
          <Rect width={CARD_SIZE.width} height={CARD_SIZE.height} opacity={100} fill="#ffffff00" />
          <SubmittedDrawPlayerImage
            drawing={drawing}
            cardSize={CARD_SIZE}
            smallCards={showSmallCards}
          />
        </Group>
      );
    },
    [initialized]
  );

  return (
    <>
      <KStage
        ref={stageRef}
        width={gameContainerDimensions.width}
        height={gameContainerDimensions.height}
      >
        <Layer>{graphData.nodes.map(renderNode)}</Layer>
      </KStage>
    </>
  );
}
